// © Microsoft Corporation. All rights reserved.

import * as React from "react";

import { ColumnArrangement, fastBreakpointManager, setCookie } from "@msnews/experiences-base";
import { HandledPropsKeys, appEnvironment, getContextData } from "@msnews/core";
import {
    MeControlComponentHandledProps,
    MeControlComponentUnhandledProps
} from "./MeControlComponent.props";
import { SSOSignInProperties, initiateSSO } from "@msnews/auth";
import { Telemetry, TelemetryActionType, TelemetryBehaviorType } from "@msnews/telemetry-contracts";
import { canUseDOM, createScriptTag } from "@msnews/isomorphic-util";

import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { CookieConsentStatus } from "@msnews/cookie-contracts";
import { ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import Foundation from "@microsoft/fast-components-foundation-react";
import { MeControlAppErrors } from "@msnews/diagnostics";
import { PageBase } from "@msnews/experiences-page-base";
import { String } from "typescript-string-operations";
import { generateMeControlOptions } from "./MeControlComponentOptions";
import { meControlComponentContainerId } from "./MeControlComponentConstants";
import { tryGetPerfTimeline } from "@msnews/diagnostics";

/**
 * NOTE
 * This component should not be added to a new page without first contacting the Me Control team
 * Please see the instructions as https://mecontroltestpartner.azurewebsites.net/PartnerDocs/articles/guides/intro-registration.html to procure a partnerId
 * and alert the Me Control team that we will be using the Me Control on a new page
 */

/**
 * NOTE
 * This component can be tested locally but you will need to use fiddler to set certain rules and behaviors.
 * See the Readme.md file for full details
 */

declare global {
    interface Window {
        /**
         * Namespace loaded by the Me Control script used to load the Me Control element
         */
        MeControl?: any;

        /**
         * The SSO Data returned by the SSO call, which contains user information like name, email, etc.
         */
        MeControlSSOData?: Promise<any>;
    }
}

/**
 * The Me Control component
 * @class - Me Control
 * @classdesc - The Me Control component. Fetches the user login information via SSO and renders the visual UI for Me Control component.
 * @export
 * @default
 */
export class MeControlComponent extends Foundation<MeControlComponentHandledProps, MeControlComponentUnhandledProps, {}> {
    /**
     * The Me Control handled prop contract
     * Describes the object that enumerates all handled props for a component. This
     * object includes all props that can in some way consumed or manipulated by the component
     * @protected
     * @type { HandledPropsKeys<IMeControlComponentHandledProps> }
     */
    protected handledProps: HandledPropsKeys<MeControlComponentHandledProps> = {
        managedClasses: undefined,
        partnerId: undefined,
        useDarkTheme: undefined,
        signedInNarrowPageDisplayMode: undefined,
        signedOutNarrowPageDisplayMode: undefined,
        signedInWidePageDisplayMode: undefined,
        signedOutWidePageDisplayMode: undefined,
        signInTooltipText: undefined,
        telemetryTag: undefined,
        requestContext: undefined
    };

    /** Determines if the last theme rendered was dark. */
    private lastRenderedUseDarkTheme?: boolean;

    /**
     * True of the user is signed in, else false.  Set after the sso call returns.
     */
    private userIsSignedIn: boolean;

    /**
     * The last display mode set, used to not repeatedly set the same displayMode
     */
    private lastDisplayModeSet: string;

    /**
     * The resolver to indicate that the meControlScript has been loaded
     */
    private meControlScriptLoadedResolver: () => void;

    /**
     * A promise that should be resolved when the meControlScript has been loaded
     * The promise constructor here sets this.meControlScriptLoadedResolver as its resolver,
     * so the Promise will resolve when this.meControlScriptLoadedResolver() is called.
     */
    private meControlScriptIsLoaded: Promise<void> = new Promise((resolve) => {
        this.meControlScriptLoadedResolver = resolve;
    });

    /**
     * Renders the containing div for the Me Control component
     * @method
     * @returns {JSX.Element} The Me Control JSX Element
     */
    public render(): JSX.Element {
        if (this.props.useDarkTheme !== this.lastRenderedUseDarkTheme && window.MeControl) {
            const themeType: string = this.props.useDarkTheme ? "dark" : "light";
            window.MeControl.API.setTheme({ type: themeType });
            this.lastRenderedUseDarkTheme = this.props.useDarkTheme;
        }

        return (
            <div id={meControlComponentContainerId}
                className={this.generateClassNames(this.props.managedClasses.meControlComponent)}
                {...this.unhandledProps()}
                {...this.props.telemetryTag}
                onClick={(e: React.MouseEvent) => {
                    Telemetry.sendActionEvent((e.currentTarget as HTMLElement).parentElement, TelemetryActionType.Click, TelemetryBehaviorType.Undefined);
                }}
                data-clarity-mask={appEnvironment.ClarityEnabled ? "true" : null}
            >
            </div>
        );
    }

    /**
     * Callend when the component has mounted
     */
    public componentDidMount(): void {
        this.loadMeControl();
        fastBreakpointManager().subscribe(this.onBreakpointCallback.bind(this));
        return;
    }

    /**
     * Unsubscribe from breakpoint changes
     */
    public componentWillUnmount(): void {
        fastBreakpointManager().unsubscribe(this.onBreakpointCallback.bind(this));
    }

    /**
     * Initialize the sso call and invoke the Me Control script when it returns
     */
    private async loadMeControl(): Promise<void> {
        if (!canUseDOM()) {
            return;
        }

        const dataConnector: ExperiencesSharedStateConnector = await PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
        const cookieConsentStatus: CookieConsentStatus = dataConnector ? dataConnector.getCurrentState().cookieConsentStatus : CookieConsentStatus.Required;
        if (cookieConsentStatus === CookieConsentStatus.Required) {
            // If the user has not given consent for us to drop cookies, assume they are signed out
            this.userIsSignedIn = false;
            this.generateMeControl({ ssoStatus: "sso_failed" });
        } else {
            if (window.MeControlSSOData) {
                // if the SSO process has already been started, wait for it to return
                const ssoData: any = await window.MeControlSSOData;
                this.userIsSignedIn = ssoData.ssoStatus === "sso_completed";
                this.generateMeControl(ssoData);
            } else {
                // if the sso process has not been started, start it now
                initiateSSO(
                    async (ssoData) => {
                        // If the sso did not return then we cannot load the Me Control
                        if (!ssoData) {
                            Telemetry.sendAppErrorEvent({
                                ...MeControlAppErrors.SSOFailedError,
                                message: "SSO did not return valid data"
                            });
                            return;
                        }

                        this.userIsSignedIn = (ssoData.ssoStatus === "sso_completed");
                        this.generateMeControl(ssoData);
                    }
                );
            }
        }

        // Generate the MeControlScriptUrl to use
        const meControlScriptUrl: string = String.Format("https://mem.gfx.ms/meversion?partner={0}&market={1}", this.props.partnerId, appEnvironment.CurrentMarket);

        // Add the Me Control script tag to the page
        const meControlScript: HTMLScriptElement = createScriptTag(meControlScriptUrl, "me-control-script");

        // Add an event listener to respond when the Me Control script has loaded
        meControlScript.addEventListener("load", () => {
            // Register listener to be perform cleanup before signout
            if (window.MeControl) {
                window.MeControl.API.addEventListener("beforesignoutfromapp", this.onBeforeSignOut);
            }

            // Resolve the meControlScriptIsLoaded promise to allow the initiateSSO callback to complete execution
            this.meControlScriptLoadedResolver();
        });

        document.body.appendChild(meControlScript);
    }

    /**
     * Generates the actual Me Control component and places it on the page
     * @param ssoData The ssoData to generate the Me Control with
     */
    private async generateMeControl(ssoData: SSOSignInProperties): Promise<void> {
        // Construct the meControlOptions to use
        const locationHref = getContextData(this.props.requestContext.data.locationHref, "locationHref");
        const meControlOptions = generateMeControlOptions(ssoData, this.props.useDarkTheme, locationHref);

        // If no user account was found, set the tooltip to invite the user to sign in
        if (!meControlOptions.currentAccount) {
            if (this.props.signInTooltipText) {
                const meControlContainer = document.getElementById(meControlComponentContainerId);
                if (meControlContainer) {
                    meControlContainer.setAttribute("title", this.props.signInTooltipText);
                }
            }
        }

        this.lastRenderedUseDarkTheme = this.props.useDarkTheme;

        // Wait for the Me Control script to be loaded
        await this.meControlScriptIsLoaded;

        this.setDisplayMode();
        this.setMeControlThemeOverride();

        // Load the Me Control and show it on the page
        const loadSuccess: boolean = window.MeControl.Loader.load(meControlOptions);
        if (!loadSuccess) {
            Telemetry.sendAppErrorEvent({
                ...MeControlAppErrors.MeControlLoadFailedError,
                message: "The Me Control Loader failed to load the Me Control"
            });
        }
    }

    /**
     * Set the display mode based on the displayMode props sent in (if any)
     */
    private setDisplayMode(): void {
        if (!window.MeControl || !window.MeControl.API) {
            return;
        }

        const isNarrowPage: boolean = (fastBreakpointManager().currentColumnArrangement <= ColumnArrangement.c2);

        // Update the display mode if the appropriate prop has been set
        if (isNarrowPage) {
            if (this.userIsSignedIn && this.props.signedInNarrowPageDisplayMode && (this.props.signedInNarrowPageDisplayMode !== this.lastDisplayModeSet)) {
                this.lastDisplayModeSet = this.props.signedInNarrowPageDisplayMode;
                window.MeControl.API.setDisplayMode(this.props.signedInNarrowPageDisplayMode);
            } else if (!this.userIsSignedIn && this.props.signedOutNarrowPageDisplayMode && (this.props.signedOutNarrowPageDisplayMode !== this.lastDisplayModeSet)) {
                this.lastDisplayModeSet = this.props.signedOutNarrowPageDisplayMode;
                window.MeControl.API.setDisplayMode(this.props.signedOutNarrowPageDisplayMode);
            }
        } else {
            if (this.userIsSignedIn && this.props.signedInWidePageDisplayMode && (this.props.signedInWidePageDisplayMode !== this.lastDisplayModeSet)) {
                this.lastDisplayModeSet = this.props.signedInWidePageDisplayMode;
                window.MeControl.API.setDisplayMode(this.props.signedInWidePageDisplayMode);
            } else if (!this.userIsSignedIn && this.props.signedOutWidePageDisplayMode && (this.props.signedOutWidePageDisplayMode !== this.lastDisplayModeSet)) {
                this.lastDisplayModeSet = this.props.signedOutWidePageDisplayMode;
                window.MeControl.API.setDisplayMode(this.props.signedOutWidePageDisplayMode);
            }
        }
    }

    /**
     * Override the Me Control theme to "dark" if in dark mode
     * This is required because the Me Control Options API is bugged,
     * and does not fully convert the Me Control to dark mode when first initialized
     */
    private setMeControlThemeOverride(): void {
        if (window.MeControl && this.props.useDarkTheme === true) {
            window.MeControl.API.setTheme({ type: "dark" });
            this.lastRenderedUseDarkTheme = true;
        }
    }

    /**
     * Update the Me Control display mode according to the breakpoints (if displayMode should be set)
     */
    private onBreakpointCallback(): void {
        this.setDisplayMode();
    }

    /**
     * Perform required tasks before user signout
     */
    private onBeforeSignOut(): void {
        // clear bingwebsso cookie before user signout. This will allow to initiate Bing WebSSO on next sign-in
        setCookie("bwssoc", "0", -1);
    }
}